// अभ्यास · the practice hub, laid out like the Sanskrit practice page: a page header, Today's practice on a dark card
// (due reviews first, three levels) with three side cards (review list, dictionary, progress), the grammar games under
// the topic filter in one grid of cards, and where the learner's memory stands. Kids see their friendlier games first.
// The chosen topics and the विकल्प choice live in the URL (?topic=…&vikalp=1) and travel into each game's link.

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { ModeInfo } from '@/prakrit/components/practice/practice-catalog'
import type { ModeAvailability, PracticeMode } from '@/prakrit/components/practice/practice-types'
import type { MasteryRecord } from '@/prakrit/lib/client/progress'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import {
    ArrowRight,
    BookOpen,
    Bookmark,
    CalendarCheck,
    Gauge,
    MessageCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { useProgress } from '@/prakrit/lib/client/progress'
import { useAudio } from '@/prakrit/lib/client/audio'
import { Bi } from '@/prakrit/components/common/bi'
import { ModeCard } from '@/prakrit/components/practice/mode-card'
import { TopicFilter } from '@/prakrit/components/practice/topic-filter'
import { LEVELS, PRACTICE_MODES } from '@/prakrit/components/practice/practice-types'
import { LEVEL_INFO, MODES, MIXED_INFO, UNAVAILABLE } from '@/prakrit/components/practice/practice-catalog'

import {
    TOPICS,
    TOPIC_BY_ID,
    VIKALP_PARAM,
    practiceHref,
    topicQuery,
    topicsForMode
} from '@/prakrit/components/practice/practice-topics'

interface PracticeHubProps {
    availability: Record<PracticeMode, ModeAvailability>
    /** Topics from the URL (?topic=). */
    initialTopics: string[]
    /** The विकल्प choice from the URL (?vikalp=1). */
    initialVikalp: boolean
}

/** What each game trains, in the order Adults see them; the label is the card's footer. */
const GROUPS: { title: L, modes: PracticeMode[] }[] = [
    { title: { hi: 'क्रिया-रूप', en: 'Verb forms' }, modes: ['dhatu-chunauti', 'kaal-pahchan', 'roop-sadhana', 'kridanta-pahchan'] },
    { title: { hi: 'वाक्य', en: 'Sentences' }, modes: ['vakya-nirmata', 'vachan-badlo', 'anuvad'] },
    { title: { hi: 'शब्द और रूप', en: 'Words and forms' }, modes: ['vibhakti-chayan', 'sanskrit-prakrit', 'sandhi-nirmata', 'samas-pahchan'] },
    { title: { hi: 'सुनना और पढ़ना', en: 'Listening and reading' }, modes: ['shravan', 'katha-kram'] }
]

const GROUP_OF = new Map<PracticeMode, L>(GROUPS.flatMap(g => g.modes.map(m => [m, g.title] as const)))
const ADULT_ORDER = GROUPS.flatMap(g => g.modes)

const MEMORY: { state: MasteryRecord['state'], label: L, tone: string }[] = [
    { state: 'new', label: { hi: 'नया', en: 'New' }, tone: 'bg-muted text-muted-foreground' },
    { state: 'learning', label: { hi: 'सीख रहे', en: 'Learning' }, tone: 'bg-saffron-soft text-saffron-strong' },
    { state: 'needs_practice', label: { hi: 'अभ्यास चाहिए', en: 'Needs practice' }, tone: 'bg-terracotta-soft text-terracotta' },
    { state: 'strong', label: { hi: 'मज़बूत', en: 'Strong' }, tone: 'bg-ink-soft text-ink' },
    { state: 'mastered', label: { hi: 'सिद्ध', en: 'Mastered' }, tone: 'bg-tulsi-soft text-tulsi' }
]

const hiDigits = (value: number): string => String(value).replace(/[0-9]/g, d => String.fromCharCode(0x0966 + Number(d)))

/** Hindi and English side by side in the heading face (Bi's `hindi` class would override the heading font). */
const HeadingPair: FC<L> = ({ hi, en }): ReactNode => (
    <span className='inline-flex flex-wrap items-baseline gap-x-2'>
        <span lang='hi' data-ex='hi'>{hi}</span>
        <span aria-hidden='true' className='ex-sep text-muted-foreground/60'>·</span>
        <span lang='en' data-ex='en'>{en}</span>
    </span>
)

const SIDE_CARD = 'flex flex-1 items-start gap-3 rounded-[1.75rem] border border-border bg-card p-5 shadow-sm transition-colors hover:border-saffron'

interface TodayCardProps {
    due: number
    listed: number
    anyOpen: boolean
    kids: boolean
    known: boolean
    filtered: boolean
    /** विकल्प सहित: Today's practice passes the choice on to its games. */
    vikalp: boolean
}

const TodayCard: FC<TodayCardProps> = ({ due, listed, anyOpen, kids, known, filtered, vikalp }): ReactNode => (
    <section aria-labelledby='today-heading' className='relative overflow-hidden rounded-[2rem] bg-ink p-6 text-paper shadow-lg lg:col-span-2'>
        <div className='ornament pointer-events-none absolute -right-8 -bottom-8 size-44 opacity-25' aria-hidden='true' />
        <span className='flex size-12 items-center justify-center rounded-2xl bg-saffron text-ink' aria-hidden='true'>
            <CalendarCheck className='size-6' />
        </span>
        <h2 id='today-heading' className='mt-4 flex flex-col'>
            <span lang='hi' className='literary text-3xl'>{MIXED_INFO.name.hi}</span>
            <span lang='en' className='text-lg text-paper/85'>{MIXED_INFO.name.en}</span>
        </h2>
        <div className='mt-2 max-w-lg text-sm text-paper/75'>
            {!known ? (
                <span className='block h-5 w-56 animate-pulse rounded bg-paper/15' aria-hidden='true' />
            ) : due > 0 || listed > 0 ? (
                <Bi
                    hi={`${due > 0 ? `${hiDigits(due)} रूप दोहराने का समय आ गया है` : 'कोई रूप अभी देय नहीं'}${listed > 0 ? `, ${hiDigits(listed)} आपकी दोहराव सूची में` : ''}। पहले इन्हीं का अभ्यास होगा, फिर अलग-अलग खेलों से।`}
                    en={`${due > 0 ? `${due} ${due === 1 ? 'item is' : 'items are'} due` : 'Nothing is due yet'}${listed > 0 ? `, ${listed} on your review list` : ''}. They come first, then a mix of games.`}
                    enClassName='text-paper/75'
                />
            ) : (
                <Bi
                    hi={kids ? 'आज अलग-अलग खेलों से मिला-जुला खेल!' : '10 प्रश्नों का मिला-जुला दौर, अलग-अलग खेलों से।'}
                    en={kids ? 'Today: a mix of all your games!' : 'A mixed round of 10 questions from different games.'}
                    enClassName='text-paper/75'
                />
            )}
        </div>

        {anyOpen ? (
            <ul className='relative mt-5 flex flex-wrap gap-2' aria-label='आज का अभ्यास, स्तर चुनें · Today’s practice: choose a level'>
                {LEVELS.map(level => {
                    const first = level === 'arambh'

                    return (
                        <li key={level}>
                            <Link
                                href={practiceHref('aaj', level, [], vikalp)}
                                className={cn(
                                    'inline-flex h-11 items-center gap-2 rounded-2xl px-5 font-bold transition-colors',
                                    'focus-visible:ring-2 focus-visible:ring-saffron focus-visible:ring-offset-2 focus-visible:ring-offset-ink focus-visible:outline-none',
                                    first ? 'bg-saffron text-ink hover:bg-saffron/90' : 'border border-paper/25 text-paper hover:bg-paper/10'
                                )}
                            >
                                <span lang='hi' className='hindi'>{LEVEL_INFO[level].name.hi}</span>
                                <span aria-hidden='true' className={first ? 'text-ink/60' : 'text-paper/50'}>·</span>
                                <span lang='en' className='text-sm font-semibold'>{LEVEL_INFO[level].name.en}</span>
                                {first && <ArrowRight className='size-4' aria-hidden='true' />}
                            </Link>
                        </li>
                    )
                })}
            </ul>
        ) : (
            <p className='relative mt-5 rounded-2xl bg-paper/10 px-4 py-3 text-sm'>
                <Bi hi={UNAVAILABLE.no_verified.hi} en={UNAVAILABLE.no_verified.en} enClassName='text-paper/75' always />
            </p>
        )}

        {filtered && (
            <p className='relative mt-3 text-xs text-paper/70'>
                <Bi hi='आज का अभ्यास चुने गए विषय नहीं मानता; यह दोहराव और मिले-जुले खेलों से बनता है।' en="Today's practice ignores the chosen topics; it is built from your reviews and a mix of games." enClassName='text-paper/70' />
            </p>
        )}
    </section>
)

export const PracticeHub: FC<PracticeHubProps> = ({ availability, initialTopics, initialVikalp }): ReactNode => {
    const router = useRouter()
    const { mode, hydrated } = usePrefs()
    const { ready, snapshot, review } = useProgress()
    const { voiceSupported, voiceEnabled, setVoiceEnabled } = useAudio()
    const [now, setNow] = useState<number | null>(null)
    const [chosen, setChosen] = useState<string[]>(initialTopics)
    const [vikalp, setVikalp] = useState(initialVikalp)
    const kids = hydrated && mode === 'kids'

    useEffect(() => {
        setNow(Date.now())
    }, [])

    // Kids see the four tenses and वचन only; anything else in the URL is set aside while in Kids mode.
    const topics = kids ? chosen.filter(id => TOPIC_BY_ID[id]?.kids) : chosen

    const writeUrl = useCallback((nextTopics: string[], nextVikalp: boolean) => {
        // Keep the catalogue order so the URL stays stable.
        const ordered = TOPICS.filter(t => nextTopics.includes(t.id)).map(t => t.id)
        const query: Record<string, string> = {}

        if (ordered.length > 0) query.topic = topicQuery(ordered)
        if (nextVikalp) query[VIKALP_PARAM] = '1'

        setChosen(ordered)
        setVikalp(nextVikalp)
        router.replace({ pathname: '/practice', query }, undefined, { shallow: true, scroll: false }).catch(() => undefined)
    }, [router])

    const toggle = (id: string) => writeUrl(chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id], vikalp)
    const clear = () => writeUrl([], vikalp)
    const chooseVikalp = (on: boolean) => writeUrl(chosen, on)

    const known = ready && now !== null
    const due = now === null ? 0 : snapshot.mastery.filter(m => m.dueAt && Date.parse(m.dueAt) <= now).length
    const listed = ready ? review.length : 0
    const anyOpen = PRACTICE_MODES.some(m => LEVELS.some(l => availability[m].levels[l]))
    const fitting = topics.length === 0 ? 0 : MODES.filter(m => topicsForMode(m.mode, topics).length > 0).length
    const voice = { supported: voiceSupported, enabled: voiceEnabled, onEnable: () => setVoiceEnabled(true) }
    const games = kids
        ? [...MODES.filter(m => m.kids), ...MODES.filter(m => !m.kids)]
        : ADULT_ORDER.map(m => MODES.find(x => x.mode === m)).filter((m): m is ModeInfo => Boolean(m))
    const counts = Object.fromEntries(MEMORY.map(({ state }) => [state, ready ? snapshot.mastery.filter(m => m.state === state).length : 0]))

    return (
        <div className='w-full'>
            <header className='mx-auto max-w-6xl px-4 pt-8 pb-6 sm:px-6 sm:pt-12'>
                <p lang='hi' className='literary text-lg text-saffron-strong'>खेल-खेल में प्राकृत</p>
                <h1 className='mt-1 font-heading text-3xl text-foreground sm:text-4xl'>
                    <HeadingPair hi='अभ्यास' en='Practice' />
                </h1>
                <div className='mt-3 max-w-3xl text-base text-muted-foreground sm:text-lg'>
                    <Bi
                        hi='रोज़ का दोहराव आपकी ग़लतियों से सीखता है। हर खेल व्याकरण-इंजन से बनता है और प्राकृत व्याकरण के नियमों से जाँचा जाता है।'
                        en='Your daily review learns from your mistakes. Every game is built by the grammar engine and checked by the rules of Prakrit grammar.'
                    />
                </div>
            </header>

            <div className='mx-auto max-w-6xl space-y-10 px-4 pb-16 sm:px-6'>
                <div className='grid gap-4 lg:grid-cols-3'>
                    <TodayCard due={due} listed={listed} anyOpen={anyOpen} kids={kids} known={known} filtered={topics.length > 0} vikalp={vikalp} />

                    <div className='flex flex-col gap-4'>
                        <Link href='/progress#due-reviews-heading' className={SIDE_CARD}>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-terracotta-soft text-terracotta' aria-hidden='true'>
                                <Bookmark className='size-5' />
                            </span>
                            <span className='flex min-w-0 flex-col'>
                                <Bi hi='मेरी दोहराव सूची' en='My review list' layout='inline' className='font-semibold' />
                                <Bi
                                    hi={`${hiDigits(listed)} रूप आपने सहेजे`}
                                    en={`${listed} ${listed === 1 ? 'form' : 'forms'} you saved`}
                                    className='text-sm text-muted-foreground'
                                />
                            </span>
                        </Link>
                        <Link href='/vocabulary' className={SIDE_CARD}>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-saffron-soft text-saffron-strong' aria-hidden='true'>
                                <BookOpen className='size-5' />
                            </span>
                            <span className='flex min-w-0 flex-col'>
                                <Bi hi='शब्दकोश' en='Dictionary' layout='inline' className='font-semibold' />
                                <Bi hi='हर शब्द, उसका चित्र और उसके सब रूप' en='Every word with its picture and all its forms' className='text-sm text-muted-foreground' />
                            </span>
                        </Link>
                        <Link href='/progress' className={SIDE_CARD}>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-tulsi-soft text-tulsi' aria-hidden='true'>
                                <Gauge className='size-5' />
                            </span>
                            <span className='flex min-w-0 flex-col'>
                                <Bi hi='मेरी प्रगति' en='My progress' layout='inline' className='font-semibold' />
                                <Bi hi='पूरे पाठ, कमज़ोर विषय और बैज' en='Lessons done, weak topics and badges' className='text-sm text-muted-foreground' />
                            </span>
                        </Link>
                    </div>
                </div>

                <section aria-labelledby='games-heading'>
                    <div className='mb-5 flex flex-wrap items-end justify-between gap-3'>
                        <div>
                            <p lang='hi' className='hindi text-sm font-semibold text-saffron-strong'>व्याकरण के खेल</p>
                            <h2 id='games-heading' className='font-heading text-3xl'>
                                {kids ? <HeadingPair hi='तुम्हारे खेल' en='Your games' /> : 'Grammar games'}
                            </h2>
                        </div>
                        <p className='text-sm text-muted-foreground'>
                            <Bi hi='हर खेल में ५०० तक अभ्यास, १०-१० के सेट में।' en='Up to 500 exercises in every game, in sets of 10.' layout='inline' enClassName='text-muted-foreground' />
                        </p>
                    </div>

                    {!hydrated ? (
                        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3' aria-hidden='true'>
                            <span className='h-40 animate-pulse rounded-[1.75rem] bg-muted sm:col-span-2 lg:col-span-3' />
                            {Array.from({ length: 6 }, (_, i) => <span key={i} className='h-56 animate-pulse rounded-[1.75rem] bg-muted' />)}
                        </div>
                    ) : (
                        <>
                            <div className='mb-5'>
                                <TopicFilter
                                    selected={topics}
                                    onToggle={toggle}
                                    onClear={clear}
                                    kids={kids}
                                    fitting={fitting}
                                    vikalp={vikalp}
                                    onVikalp={chooseVikalp}
                                />
                            </div>
                            <ul className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                                {games.map(info => {
                                    const applied = topicsForMode(info.mode, topics)

                                    return (
                                        <li key={info.mode}>
                                            <ModeCard
                                                info={info}
                                                availability={availability[info.mode]}
                                                kids={kids}
                                                group={GROUP_OF.get(info.mode) ?? { hi: 'खेल', en: 'Game' }}
                                                topics={applied}
                                                dimmed={topics.length > 0 && applied.length === 0}
                                                voice={voice}
                                                vikalp={vikalp}
                                            />
                                        </li>
                                    )
                                })}
                            </ul>
                        </>
                    )}
                </section>

                <section aria-labelledby='memory-heading' className='rounded-[2rem] border border-border bg-card p-6 shadow-sm'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <div className='flex items-center gap-3'>
                            <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-ink-soft text-ink' aria-hidden='true'>
                                <MessageCircle className='size-5' />
                            </span>
                            <div>
                                <h2 id='memory-heading' className='font-semibold'>
                                    <Bi hi='आपकी स्मृति-स्थिति' en='Where your memory stands' layout='inline' />
                                </h2>
                                <Bi hi='हर शब्द और रूप की अलग स्थिति रखी जाती है।' en='Every word and form is tracked on its own.' className='text-sm text-muted-foreground' />
                            </div>
                        </div>
                        <Link href='/progress' className='inline-flex min-h-tap items-center gap-1 text-sm font-semibold text-saffron-strong'>
                            <Bi hi='पूरी प्रगति देखें' en='See full progress' layout='inline' enClassName='text-saffron-strong' />
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                    <ul className='mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5'>
                        {MEMORY.map(({ state, label, tone }) => (
                            <li key={state} className={cn('rounded-2xl p-4', tone)}>
                                <p className='text-2xl font-bold tabular-nums'>{counts[state]}</p>
                                <Bi hi={label.hi} en={label.en} className='text-sm font-medium' enClassName='text-current opacity-80' />
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </div>
    )
}
